// pages/ProblemPage.jsx
// Full problem detail view.
// Uses LatexRenderer to render LaTeX math in all text fields.
// Rating tab calls the real API.

import React, { useState, useEffect } from "react";
import DiffPip        from "../components/atoms/DiffPip";
import { TopicBadge, TagBadge } from "../components/atoms/Badges";
import FirmChip       from "../components/atoms/FirmChip";
import LatexRenderer  from "../components/atoms/LatexRenderer";
import { useTheme }   from "../context/ThemeContext";
import { useAuth  }   from "../context/AuthContext";

export default function ProblemPage({ p, onBack, nav, onRatingSubmit }) {
  const { t }    = useTheme();
  const { user } = useAuth();

  const [tab,    setTab]    = useState("problem");
  const [rating, setRating] = useState({ difficulty: null, clarity: null });
  const [done,   setDone]   = useState(false);
  const [busy,   setBusy]   = useState(false);
  const [shareLabel, setShareLabel] = useState("Share");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${p.title} | QuantSoc`;
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement("meta");
      description.name = "description";
      document.head.appendChild(description);
    }
    description.content = p.preview || "QuantSoc problem archive";

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}/problem/${encodeURIComponent(p.id)}`;

    return () => {
      document.title = "QuantSoc | Quant Interview Questions and Solutions";
    };
  }, [p]);

  const shareProblem = async () => {
    const url = `${window.location.origin}/problem/${encodeURIComponent(p.id)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: p.title, text: p.preview, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShareLabel("Copied");
        window.setTimeout(() => setShareLabel("Share"), 1600);
      }
    } catch (err) {
      if (err.name !== "AbortError") console.error("Share failed:", err.message);
    }
  };

  const submitRating = async () => {
    if (!rating.difficulty || !rating.clarity) return;
    setBusy(true);
    try {
      await onRatingSubmit(p.id, rating.difficulty, rating.clarity);
      setDone(true);
    } catch (e) {
      console.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  // ── Section block wrapper ──────────────────────────────────────────
  const Block = ({ children }) => (
    <div style={{ background: t.bg2, borderRadius: 14, padding: "24px 28px", borderLeft: `3px solid ${t.text}`, marginBottom: 24 }}>
      {children}
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: t.bg }}>
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "82px 32px 100px" }}>

        {/* ── Back button ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, paddingBottom: 26 }}>
          <button onClick={onBack}
            style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: t.text3, fontSize: 13, padding: 0, transition: "color 0.15s" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = t.text)}
            onMouseLeave={(e) => (e.currentTarget.style.color = t.text3)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 13L5 8l5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to Archive
          </button>
          <button onClick={shareProblem} title="Copy or share this problem link"
            style={{ background: t.bg2, border: `0.5px solid ${t.border}`, borderRadius: 8, cursor: "pointer", color: t.text2, fontSize: 12, padding: "7px 11px" }}>
            {shareLabel}
          </button>
        </div>

        {/* ── Header ── */}
        <div style={{ borderBottom: `0.5px solid ${t.border}`, paddingBottom: 28 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontSize: 10, color: t.text3, fontFamily: "monospace", letterSpacing: "0.08em" }}>{p.id}</span>
            <DiffPip level={p.difficulty} />
            <TopicBadge label={p.topic} />
            <span style={{ fontSize: 11, color: t.text3 }}>{new Date(p.dateAdded).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
            {p.addedBy?.username && <span style={{ fontSize: 11, color: t.text3 }}>· added by {p.addedBy.username}</span>}
          </div>
          {/* Title — may contain LaTeX */}
          <h1 style={{ fontSize: 32, fontWeight: 800, color: t.text, margin: "0 0 18px", fontFamily: "'Playfair Display',Georgia,serif", letterSpacing: "-0.03em", lineHeight: 1.15 }}>
            <LatexRenderer text={p.title} />
          </h1>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
            {p.tags.map((tg) => <TagBadge key={tg} label={tg} />)}
          </div>
          {(p.askedIn || []).length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: t.text3, fontWeight: 500 }}>Asked at:</span>
              {p.askedIn.map((f) => <FirmChip key={f} name={f} showName={true} />)}
            </div>
          )}
        </div>

        {/* ── Stats ── */}
        <div style={{ display: "flex", gap: 14, margin: "24px 0 0", flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ background: t.bg2, borderRadius: 9, padding: "10px 16px" }}>
            <p style={{ fontSize: 10, color: t.text3, margin: "0 0 3px", letterSpacing: "0.05em" }}>DIFFICULTY RATING</p>
            <p style={{ fontSize: 15, fontWeight: 600, color: t.text, margin: 0 }}>
              ★ {p.peerRating ? p.peerRating.toFixed(1) : (p.adminRating || 3).toFixed(1)}
            </p>
          </div>
          {p.peerRating && (
            <p style={{ fontSize: 11, color: t.text3, margin: 0, lineHeight: 1.5 }}>
              {`${p.ratingCount || 0} rating${(p.ratingCount || 0) !== 1 ? "s" : ""}`}
            </p>
          )}
        </div>

        {/* ── Tabs ── */}
        <div style={{ display: "flex", borderBottom: `0.5px solid ${t.border}`, marginTop: 24, marginBottom: 32 }}>
          {[["problem","Problem Statement"],["solution","Solution"],["review","Leave Review"]].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "14px 18px 12px", fontSize: 13, fontWeight: tab === k ? 600 : 400, color: tab === k ? t.text : t.text3, borderBottom: `2px solid ${tab === k ? t.text : "transparent"}`, transition: "all 0.15s" }}>
              {l}
            </button>
          ))}
        </div>

        {/* ══ PROBLEM TAB ══ */}
        {tab === "problem" && (
          <div style={{ maxWidth: 720 }}>
            {/* Full problem statement rendered with LaTeX */}
            <Block>
              <p style={{ fontSize: 15, lineHeight: 1.9, margin: 0, fontFamily: "Georgia,serif" }}>
                <LatexRenderer text={p.fullStatement || p.preview} />
              </p>
            </Block>

            {/* Approach / hint */}
            {p.approach && (
              <div style={{ marginTop: 8 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: t.text3, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 10 }}>
                  Suggested Approach
                </p>
                <p style={{ fontSize: 14, color: t.text2, lineHeight: 1.85, margin: 0, fontFamily: "Georgia,serif" }}>
                  <LatexRenderer text={p.approach} />
                </p>
              </div>
            )}
          </div>
        )}

        {/* ══ SOLUTION TAB ══ */}
        {tab === "solution" && (
          <div style={{ maxWidth: 720 }}>
            {!user ? (
              <div style={{ textAlign: "center", padding: "80px 24px" }}>
                <svg width="52" height="52" viewBox="0 0 52 52" fill="none" style={{ display: "block", margin: "0 auto 18px" }}>
                  <rect x="8" y="24" width="36" height="24" rx="5" fill={t.bg2} stroke={t.border} strokeWidth="1" />
                  <path d="M18 24V19a8 8 0 0 1 16 0v5" stroke={t.border2} strokeWidth="1.5" fill="none" strokeLinecap="round" />
                  <circle cx="26" cy="36" r="3.5" fill={t.border2} />
                </svg>
                <p style={{ fontSize: 16, color: t.text, fontWeight: 600, marginBottom: 8 }}>Sign in to view the solution</p>
                <p style={{ fontSize: 13, color: t.text3, marginBottom: 20 }}>Create an account or sign in to access published solutions.</p>
                <button onClick={() => nav("login")} style={{ background: t.primary, color: t.primaryFg, border: "none", borderRadius: 9, padding: "10px 24px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Sign in
                </button>
              </div>
            ) : p.solutionRevealed ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#2d7a4f" }} />
                  <span style={{ fontSize: 11, color: "#2d7a4f", fontWeight: 700, letterSpacing: "0.07em" }}>SOLUTION PUBLISHED</span>
                </div>

                {/* Approach recap */}
                {p.approach && (
                  <div style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Method</p>
                    <p style={{ fontSize: 14, color: t.text2, lineHeight: 1.85, margin: 0, fontFamily: "Georgia,serif" }}>
                      <LatexRenderer text={p.approach} />
                    </p>
                  </div>
                )}

                {/* Full solution with LaTeX */}
                <div style={{ background: t.bg2, borderRadius: 12, padding: "20px 24px", border: `0.5px solid ${t.border}` }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Solution</p>
                  <div style={{ fontSize: 15, lineHeight: 2, color: t.text, fontFamily: "Georgia,serif" }}>
                    <LatexRenderer text={p.solution} />
                  </div>
                </div>
              </>
            ) : (
              /* Locked solution */
              <div style={{ textAlign: "center", padding: "80px 0" }}>
                <svg width="52" height="52" viewBox="0 0 52 52" fill="none" style={{ display: "block", margin: "0 auto 18px" }}>
                  <rect x="8" y="24" width="36" height="24" rx="5" fill={t.bg2} stroke={t.border} strokeWidth="1" />
                  <path d="M18 24V19a8 8 0 0 1 16 0v5" stroke={t.border2} strokeWidth="1.5" fill="none" strokeLinecap="round" />
                  <circle cx="26" cy="36" r="3.5" fill={t.border2} />
                </svg>
                <p style={{ fontSize: 16, color: t.text, fontWeight: 600, marginBottom: 8 }}>Solution not yet revealed</p>
                <p style={{ fontSize: 13, color: t.text3 }}>The admin will publish the solution at a later date.</p>
              </div>
            )}
          </div>
        )}

        {/* ══ REVIEW TAB ══ */}
        {tab === "review" && (
          <div style={{ maxWidth: 480 }}>
            {!user ? (
              /* Not logged in */
              <div style={{ textAlign: "center", padding: "60px 24px" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: t.bg2, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <circle cx="11" cy="8" r="4" stroke={t.text2} strokeWidth="1.5" />
                    <path d="M3 20c0-4 3.6-7 8-7s8 3 8 7" stroke={t.text2} strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <p style={{ fontSize: 15, color: t.text, fontWeight: 600, margin: "0 0 8px" }}>Sign in to leave a review</p>
                <p style={{ fontSize: 13, color: t.text3, margin: "0 0 20px" }}>You need an account to rate problems.</p>
                <button onClick={() => nav("login")} style={{ background: t.primary, color: t.primaryFg, border: "none", borderRadius: 9, padding: "10px 24px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Sign in
                </button>
              </div>
            ) : done ? (
              /* Success */
              <div style={{ textAlign: "center", padding: "60px 0" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#edf7f1", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <path d="M4 11l5 5 9-9" stroke="#2d7a4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p style={{ fontSize: 16, fontWeight: 600, color: t.text, margin: "0 0 8px" }}>Review submitted</p>
                <p style={{ fontSize: 13, color: t.text3 }}>Thank you for your feedback.</p>
              </div>
            ) : (
              /* Rating form */
              <>
                <p style={{ fontSize: 13, color: t.text3, marginBottom: 28, lineHeight: 1.65 }}>Rate this problem on difficulty and clarity.</p>
                {[["difficulty","Difficulty"],["clarity","Clarity"]].map(([k,l]) => (
                  <div key={k} style={{ marginBottom: 24 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: t.text2, letterSpacing: "0.06em", marginBottom: 12 }}>{l.toUpperCase()}</p>
                    <div style={{ display: "flex", gap: 8 }}>
                      {[1,2,3,4,5].map((n) => (
                        <button key={n} onClick={() => setRating((r) => ({ ...r, [k]: n }))}
                          style={{ width: 46, height: 46, borderRadius: 10, background: rating[k] === n ? t.primary : t.bg2, color: rating[k] === n ? t.primaryFg : t.text2, border: `0.5px solid ${t.border}`, cursor: "pointer", fontSize: 15, fontWeight: 600, transition: "all 0.15s" }}>
                          {n}
                        </button>
                      ))}
                    </div>
                    <p style={{ fontSize: 11, color: t.text3, margin: "7px 0 0", display: "flex", justifyContent: "space-between" }}>
                      <span>1 = easy / unclear</span><span>5 = hard / crystal clear</span>
                    </p>
                  </div>
                ))}
                <button onClick={submitRating} disabled={busy || !rating.difficulty || !rating.clarity}
                  style={{ background: (rating.difficulty && rating.clarity) ? t.primary : t.bg2, color: (rating.difficulty && rating.clarity) ? t.primaryFg : t.text3, border: "none", borderRadius: 10, padding: "11px 28px", fontSize: 14, fontWeight: 600, cursor: (rating.difficulty && rating.clarity) ? "pointer" : "not-allowed", opacity: busy ? 0.7 : 1, transition: "all 0.2s" }}>
                  {busy ? "Submitting…" : "Submit Rating"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
